import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronRight, CircleCheck, Copy, CreditCard, Download, Info, Landmark, Loader2, TriangleAlert, Zap, BadgeCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBillingApi } from '../../../../common/hooks/use-billing-api.hook';
import { VendorInvoice, WireBankDetails, WireInterval, pickLocalizedText } from '../../../../common/models/billing.model';
import { VendorPlan } from '../../../../modules/superadmin/our-vendors/models/vendor.model';

interface PaymentMethodModalProps {
  /** The plan being purchased; the modal is open when non-null. */
  plan: VendorPlan | null;
  onClose: () => void;
  /** Fired when the vendor picks Card + a billing cycle — parent opens Stripe embedded checkout. */
  onChooseCard: (planId: string, interval: WireInterval) => void;
  /** The billing cycle the vendor already picked on the plans page. */
  initialInterval?: WireInterval;
}

type Step = 'choose' | 'card-interval' | 'wire-interval' | 'wire-invoice';

const money = (amount: number, currency: string): string => {
  const symbol = (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${(currency || '').toUpperCase()} `;
  // Whole amounts read cleanly; fees (e.g. 3% of 750 = 22.5) keep 2 decimals.
  const rounded = Math.round(amount * 100) / 100;
  const str = Number.isInteger(rounded) ? rounded.toLocaleString() : rounded.toFixed(2);
  return `${symbol}${str}`;
};

/**
 * Two-method payment chooser. Card routes to the existing Stripe embedded
 * checkout (handled by the parent). Wire creates a manual invoice and shows the
 * bank details + reference — the vendor stays on their current plan until a
 * super-admin confirms the transfer.
 */
export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({ plan, onClose, onChooseCard, initialInterval }) => {
  const { t, i18n } = useTranslation();
  const billingApi = useBillingApi();
  const [cardFeePercent, setCardFeePercent] = useState(0);
  const [annualDiscountPercent, setAnnualDiscountPercent] = useState(0);

  const [step, setStep] = useState<Step>('choose');
  const [interval, setInterval] = useState<WireInterval>('month');
  const [bankDetails, setBankDetails] = useState<WireBankDetails | null>(null);
  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const open = !!plan;

  // Reset all transient state each time the modal (re)opens.
  useEffect(() => {
    if (open) {
      setStep('choose');
      setInterval(initialInterval ?? 'month');
      setInvoice(null);
      setError(null);
      setCopied(null);
      billingApi.getBillingConfig()
        .then((c) => { setCardFeePercent(c?.cardFeePercent || 0); setAnnualDiscountPercent(c?.annualDiscountPercent || 0); })
        .catch(() => { setCardFeePercent(0); setAnnualDiscountPercent(0); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?._id]);

  const goWire = useCallback(async () => {
    setStep('wire-interval');
    if (!bankDetails) {
      try {
        const { bankDetails: bd } = await billingApi.getWireConfig();
        setBankDetails(bd || {});
      } catch {
        setBankDetails({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankDetails]);

  const createInvoice = useCallback(async () => {
    if (!plan) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await billingApi.createWireInvoice(plan._id, interval);
      setInvoice(inv);
      setStep('wire-invoice');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('BILLING.PAY_ERR_INVOICE', 'Could not create the invoice. Please try again.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, interval]);

  const copy = (label: string, value?: string) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
  };

  const bankRows: { label: string; value?: string }[] = bankDetails
    ? [
        { label: t('BILLING.BANK_ACCOUNT_NAME', 'Account name'), value: bankDetails.accountName },
        { label: t('BILLING.BANK_BANK', 'Bank'), value: bankDetails.bankName },
        { label: t('BILLING.BANK_ACCOUNT_NUMBER', 'Account number'), value: bankDetails.accountNumber },
        { label: t('BILLING.BANK_IBAN', 'IBAN'), value: bankDetails.iban },
        { label: t('BILLING.BANK_SWIFT', 'SWIFT/BIC'), value: bankDetails.swift },
        { label: t('BILLING.BANK_ROUTING', 'Routing number'), value: bankDetails.routingNumber },
        { label: t('BILLING.BANK_ADDRESS', 'Address'), value: bankDetails.address },
      ].filter((r) => r.value)
    : [];
  const refLabel = t('BILLING.PAY_REF', 'Payment reference');

  // Billing-cycle pricing (mirrors the backend: an explicit annualPrice wins,
  // else monthly×12 less the super-admin annual discount).
  const planCurrency = plan?.currency || 'usd';
  const monthlyPrice = plan?.price ?? 0;
  const annualPrice = plan?.annualPrice ?? Math.round(monthlyPrice * 12 * (1 - annualDiscountPercent / 100));
  const annualSavings = Math.max(0, monthlyPrice * 12 - annualPrice);
  const annualPerMonth = annualPrice / 12;

  // Card (Stripe) surcharge shown per cycle: base + fee (pct%) = total.
  const monthlyFee = monthlyPrice * (cardFeePercent / 100);
  const annualFee = annualPrice * (cardFeePercent / 100);
  const feeLine = (fee: number) =>
    t('BILLING.PAY_CARD_FEE_LINE', '+ {{fee}} card fee ({{pct}}%)', { fee: money(fee, planCurrency), pct: cardFeePercent });
  const totalLine = (base: number, fee: number) =>
    t('BILLING.PAY_CARD_TOTAL', '{{total}} total', { total: money(base + fee, planCurrency) });

  // One-time integration fee: added to the FIRST invoice only, on either payment
  // method, and never billed again — so it sits outside the per-cycle pricing.
  const integrationFee = plan?.integrationFee || 0;
  const setupNote = t('BILLING.PAY_SETUP_FEE', '+ {{fee}} one-time integration fee', {
    fee: money(integrationFee, planCurrency),
  });
  const setupExplainer = t(
    'BILLING.PAY_SETUP_FEE_NOTE',
    'Your first invoice includes a one-time {{fee}} integration fee covering setup. It is charged once — renewals are {{plan}} only.',
    { fee: money(integrationFee, planCurrency), plan: plan?.name },
  );

  const title =
    step === 'choose' ? t('BILLING.PAY_TITLE_CHOOSE', 'Choose a payment method')
    : step === 'wire-interval' ? t('BILLING.PAY_TITLE_WIRE', 'Bank transfer')
    : t('BILLING.PAY_TITLE_INVOICE', 'Invoice created');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1 — method chooser */}
        {step === 'choose' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t('BILLING.PAY_HOW', '{{plan}} — how would you like to pay?', { plan: plan?.name })}
            </p>
            <MethodCard
              icon={<CreditCard className="size-5" />}
              title={t('BILLING.PAY_CARD', 'Card')}
              subtitle={t('BILLING.PAY_CARD_SUB', 'Pay instantly with a credit or debit card. Activates immediately.')}
              pills={[
                { icon: <Zap className="size-3" />, label: t('BILLING.PAY_PILL_INSTANT', 'Instant activation') },
                ...(cardFeePercent > 0
                  ? [{ label: t('BILLING.PAY_CARD_BADGE', '+{{pct}}% fee', { pct: cardFeePercent }) }]
                  : []),
              ]}
              onClick={() => setStep('card-interval')}
            />
            <MethodCard
              icon={<Landmark className="size-5" />}
              title={t('BILLING.PAY_WIRE', 'Bank / wire transfer')}
              subtitle={t('BILLING.PAY_WIRE_SUB', 'Get an invoice with our bank details. Activates once we confirm your payment.')}
              pills={cardFeePercent > 0
                ? [{ icon: <BadgeCheck className="size-3" />, label: t('BILLING.PAY_WIRE_BADGE', 'No fees') }]
                : []}
              onClick={goWire}
            />
          </div>
        )}

        {/* Card — billing-cycle choice before Stripe checkout */}
        {step === 'card-interval' && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              {t('BILLING.PAY_CYCLE', 'Choose a billing cycle for the {{plan}} plan.', { plan: plan?.name })}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <CycleOption
                selected={interval === 'month'}
                onClick={() => setInterval('month')}
                title={t('BILLING.MONTHLY', 'Monthly')}
                price={`${money(monthlyPrice, planCurrency)} ${t('BILLING.PAY_PER_MONTH_UNIT', '/ month')}`}
                feeNote={cardFeePercent > 0 ? feeLine(monthlyFee) : undefined}
                totalNote={cardFeePercent > 0 ? totalLine(monthlyPrice, monthlyFee) : undefined}
                oneTimeNote={integrationFee > 0 ? setupNote : undefined}
                note={t('BILLING.PAY_BILLED_MONTHLY', 'Billed every month')}
              />
              <CycleOption
                selected={interval === 'year'}
                onClick={() => setInterval('year')}
                title={t('BILLING.ANNUAL', 'Annual')}
                price={`${money(annualPrice, planCurrency)} ${t('BILLING.PAY_PER_YEAR_UNIT', '/ year')}`}
                subnote={t('BILLING.PAY_ANNUAL_EQUIV', '≈ {{amount}} / month', { amount: money(annualPerMonth, planCurrency) })}
                feeNote={cardFeePercent > 0 ? feeLine(annualFee) : undefined}
                totalNote={cardFeePercent > 0 ? totalLine(annualPrice, annualFee) : undefined}
                oneTimeNote={integrationFee > 0 ? setupNote : undefined}
                note={annualSavings > 0
                  ? t('BILLING.PAY_SAVE', 'Save {{amount}} a year', { amount: money(annualSavings, planCurrency) })
                  : t('BILLING.PAY_BILLED_YEARLY', 'One payment for the full year')}
              />
            </div>
            {integrationFee > 0 && (
              <p className="text-xs leading-relaxed text-muted-foreground">{setupExplainer}</p>
            )}
            {cardFeePercent > 0 && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t('QUOTE.CARD_FEE_NOTE', 'Card payments add a {{pct}}% processing fee to cover our payment provider’s costs. Choose bank transfer to avoid it.', { pct: cardFeePercent })}
              </p>
            )}
            <div className="mt-2 flex justify-between gap-3">
              <Button variant="ghost" className="text-muted-foreground" onClick={() => setStep('choose')}>
                {t('BILLING.PAY_BACK', 'Back')}
              </Button>
              <Button variant="outline" onClick={() => plan && onChooseCard(plan._id, interval)}>
                {t('BILLING.PAY_CONTINUE', 'Continue to payment')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — interval choice */}
        {step === 'wire-interval' && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              {t('BILLING.PAY_CYCLE', 'Choose a billing cycle for the {{plan}} plan.', { plan: plan?.name })}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <CycleOption
                selected={interval === 'month'}
                onClick={() => setInterval('month')}
                title={t('BILLING.MONTHLY', 'Monthly')}
                price={`${money(monthlyPrice, planCurrency)} ${t('BILLING.PAY_PER_MONTH_UNIT', '/ month')}`}
                oneTimeNote={integrationFee > 0 ? setupNote : undefined}
                note={t('BILLING.PAY_BILLED_MONTHLY', 'Billed every month')}
              />
              <CycleOption
                selected={interval === 'year'}
                onClick={() => setInterval('year')}
                title={t('BILLING.ANNUAL', 'Annual')}
                price={`${money(annualPrice, planCurrency)} ${t('BILLING.PAY_PER_YEAR_UNIT', '/ year')}`}
                subnote={t('BILLING.PAY_ANNUAL_EQUIV', '≈ {{amount}} / month', { amount: money(annualPerMonth, planCurrency) })}
                oneTimeNote={integrationFee > 0 ? setupNote : undefined}
                note={annualSavings > 0
                  ? t('BILLING.PAY_SAVE', 'Save {{amount}} a year', { amount: money(annualSavings, planCurrency) })
                  : t('BILLING.PAY_BILLED_YEARLY', 'One payment for the full year')}
              />
            </div>
            {integrationFee > 0 && (
              <p className="text-xs leading-relaxed text-muted-foreground">{setupExplainer}</p>
            )}
            <div className="mt-2 flex justify-between gap-3">
              <Button variant="ghost" className="text-muted-foreground" onClick={() => setStep('choose')}>
                {t('BILLING.PAY_BACK', 'Back')}
              </Button>
              <Button variant="outline" onClick={createInvoice} disabled={loading}>
                {loading ? <Loader2 className="size-4 animate-spin" /> : t('BILLING.PAY_CREATE_INVOICE', 'Create invoice')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — invoice + bank details */}
        {step === 'wire-invoice' && invoice && (
          <div className="flex flex-col gap-4">
            <Alert>
              <Info />
              <AlertDescription>
                {t('BILLING.PAY_STILL_ON_PLAN', 'You are still on your current plan. The {{plan}} plan activates once we confirm your transfer.', { plan: invoice.planName })}
              </AlertDescription>
            </Alert>

            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">
                {invoice.invoiceNumber} · {invoice.interval === 'year' ? t('BILLING.ANNUAL', 'Annual') : t('BILLING.MONTHLY', 'Monthly')}
              </span>
              <span className="font-heading text-xl leading-none font-medium tracking-tight">
                {money(invoice.amount, invoice.currency)}
              </span>
            </div>

            <div className="overflow-hidden border">
              {/* Payment reference — the most important field to include on the wire */}
              <CopyRow
                label={refLabel}
                value={invoice.invoiceNumber}
                highlight
                copied={copied === refLabel}
                onCopy={() => copy(refLabel, invoice.invoiceNumber)}
              />
              {bankRows.map((r) => (
                <CopyRow
                  key={r.label}
                  label={r.label}
                  value={r.value}
                  copied={copied === r.label}
                  onCopy={() => copy(r.label, r.value)}
                />
              ))}
              {bankRows.length === 0 && (
                <div className="p-4">
                  <p className="text-sm text-muted-foreground">
                    {t('BILLING.PAY_NO_BANK', 'Bank details aren’t configured yet — our team will email them to you shortly.')}
                  </p>
                </div>
              )}
            </div>
            {pickLocalizedText(bankDetails?.instructions, i18n.language) && (
              <p className="text-sm leading-relaxed text-muted-foreground">
                {pickLocalizedText(bankDetails?.instructions, i18n.language)}
              </p>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => billingApi.downloadInvoicePdf(invoice._id, invoice.invoiceNumber).catch(() => setError(t('BILLING.PAY_ERR_PDF', 'Could not download the PDF. Please try again.')))}
              >
                <Download />
                {t('BILLING.DOWNLOAD_PDF', 'Download PDF')}
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                {t('BILLING.PAY_DONE', 'Done')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface Pill { label: string; icon?: React.ReactNode }

const MethodCard: React.FC<{ icon: React.ReactNode; title: string; subtitle: string; onClick: () => void; pills?: Pill[] }> = ({ icon, title, subtitle, onClick, pills = [] }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    className="group flex cursor-pointer items-center gap-4 border p-4 transition-colors hover:border-foreground/40 hover:bg-muted/50"
  >
    <div className="flex size-11 shrink-0 items-center justify-center bg-muted transition-transform group-hover:scale-105">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-base font-medium">{title}</span>
        {pills.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            {p.icon}{p.label}
          </span>
        ))}
      </div>
      <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
    </div>
    <ChevronRight className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
  </div>
);

/** A billing-cycle option card showing the price + a note. Selected reads as an ink outline. */
const CycleOption: React.FC<{ selected: boolean; onClick: () => void; title: string; price: string; note: string; subnote?: string; feeNote?: string; totalNote?: string; oneTimeNote?: string }> = ({ selected, onClick, title, price, note, subnote, feeNote, totalNote, oneTimeNote }) => (
  <div
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    className={cn(
      'cursor-pointer rounded-xl border bg-card p-4 transition-colors',
      selected ? 'border-foreground' : 'hover:border-foreground/40',
    )}
  >
    <div className="flex items-center justify-between">
      <span className="text-base font-medium">{title}</span>
      {selected && <CircleCheck className="size-[18px]" />}
    </div>
    <p className="font-heading mt-1 text-xl leading-none font-medium tracking-tight">{price}</p>
    {subnote && <p className="mt-1 text-xs text-muted-foreground">{subnote}</p>}
    {/* Card-fee breakdown: base price + fee (pct%) = total. */}
    {feeNote && <p className="mt-2 text-xs text-muted-foreground">{feeNote}</p>}
    {/* One-time setup charge — kept out of the recurring total above it. */}
    {oneTimeNote && <p className={cn('text-xs text-muted-foreground', feeNote ? '' : 'mt-2')}>{oneTimeNote}</p>}
    {totalNote && <p className="mt-1 text-sm font-medium">{totalNote}</p>}
    <p className="mt-1 text-sm text-muted-foreground">{note}</p>
  </div>
);

const CopyRow: React.FC<{ label: string; value?: string; highlight?: boolean; copied: boolean; onCopy: () => void }> = ({ label, value, highlight, copied, onCopy }) => {
  const { t } = useTranslation();
  return (
    <div className={cn('flex items-center justify-between gap-2 border-b px-4 py-2.5 last:border-b-0', highlight && 'bg-muted/50')}>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        <p className="text-sm break-all">{value}</p>
      </div>
      <Button size="icon-sm" variant="ghost" aria-label={t('BILLING.PAY_COPY', 'Copy {{label}}', { label })} onClick={onCopy}>
        {copied ? <CircleCheck className="text-success" /> : <Copy className="text-muted-foreground" />}
      </Button>
    </div>
  );
};

export default PaymentMethodModal;
